import { useCallback, useEffect, useMemo, useState } from "react";
import { BiltCashChart } from "../components/BiltCashChart";
import { PageTitle, SectionTitle } from "../components/Heading";
import {
  atLeast,
  balanceExpiry,
  buildPlan,
  EPS,
  fetchBiltCash,
  monthLabel,
  monthName,
  RULES,
  saveRent,
  summaryTiles,
  type BiltCashDto,
  type Plan,
  type PlanInputs,
  type Tone,
} from "../lib/biltCash";
import { formatExact, formatWhole } from "../lib/money";

const TONE: Record<Tone, string> = {
  accent: "text-sky-400",
  good: "text-green-400",
  warn: "text-amber-400",
  bad: "text-red-400",
  plain: "text-neutral-100",
};

const FIELD =
  "min-h-10 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 tabular-nums";

/** 1 to one milestone's worth (25,000 is a fresh start); a half-typed "-" is left alone. */
function clampPoints(text: string): string {
  const n = Number(text);
  if (text.trim() === "" || !Number.isFinite(n)) return text;
  return String(Math.min(RULES.milestonePoints, Math.max(1, n)));
}

function parseAmount(text: string): number | null {
  const n = Number(text);
  return text.trim() !== "" && Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Bilt Cash planner: how much card spend before each rent covers its unlock,
 * with each part of the balance expiring on the date Bilt states for it.
 *
 * Rent comes back from the API decrypted for this page only, and is masked
 * until Reveal. The balance and milestone figures are the last refresh
 * readings, editable here for a what-if and never saved from this page.
 */
export default function BiltCash() {
  const [data, setData] = useState<BiltCashDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cashText, setCashText] = useState("");
  const [pointsText, setPointsText] = useState("");

  const load = useCallback(() => {
    fetchBiltCash()
      .then((d) => {
        setData(d);
        setCashText(d.cash ? String(d.cash.amount) : "");
        setPointsText(String(d.points_to_next?.points ?? RULES.milestonePoints));
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(load, [load]);

  const rent = data?.rent.state === "ok" ? data.rent.amount : null;
  const inputs = useMemo(() => {
    if (!data || rent === null) return null;
    return {
      today: data.today,
      cash: parseAmount(cashText) ?? 0,
      pointsToNext: parseAmount(pointsText) ?? RULES.milestonePoints,
      rent,
      expiring: data.cash?.expiring,
    };
  }, [data, rent, cashText, pointsText]);
  const plan = useMemo(() => (inputs ? buildPlan(inputs) : null), [inputs]);

  if (error && !data) {
    return <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">{error}</div>;
  }
  if (!data) return <div className="text-sm text-neutral-400">Loading…</div>;
  const lots = data.cash?.expiring ?? [];
  const guess = balanceExpiry(data.today);

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        <PageTitle>Bilt Cash</PageTitle>
        <p className="text-sm text-neutral-400">
          Palladium · Flexible Bilt Cash option · rent unlocked at 1X
        </p>
      </div>

      {plan && inputs && <Summary plan={plan} inputs={inputs} />}

      <div className="grid gap-6 lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start">
        <div className="space-y-4 rounded-2xl border border-neutral-800/70 bg-neutral-900 p-4">
          <SectionTitle>Starting point</SectionTitle>
          <label className="flex flex-col gap-1 text-xs text-neutral-400">
            Bilt Cash balance
            <input
              id="bilt-cash-balance"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={cashText}
              onChange={(e) => setCashText(e.target.value)}
              className={FIELD}
            />
            <span className="text-neutral-500">
              {data.cash
                ? `Last refresh ${data.cash.as_of}. Edit for a what-if; nothing is saved.`
                : "No reading yet. Run /points-refresh bilt-rewards."}
            </span>
          </label>
          <div className="flex flex-col gap-1 text-xs text-neutral-400">
            When it expires
            {lots.length ? (
              <ul className="space-y-0.5 text-sm text-neutral-100 tabular-nums">
                {lots.map((l) => (
                  <li key={l.month} className="flex justify-between">
                    <span>{monthLabel(l.month)}</span>
                    <span>{formatExact(l.amount)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-sm text-neutral-100">
                Guessed: end of {monthName(guess.month)} {guess.year}
              </span>
            )}
            <span className="text-neutral-500">
              {lots.length
                ? "Read off Bilt with the balance. An edited balance comes off the oldest month, or counts as newly earned."
                : "No dated reading yet, so the earliest the plan can assume. A refresh reads Bilt's dates."}
            </span>
          </div>
          <label className="flex flex-col gap-1 text-xs text-neutral-400">
            Points to your next $50
            <input
              id="bilt-points-to-next"
              type="number"
              inputMode="numeric"
              min={1}
              max={RULES.milestonePoints}
              step={100}
              value={pointsText}
              onChange={(e) => setPointsText(clampPoints(e.target.value))}
              className={FIELD}
            />
            <span className="text-neutral-500">
              {data.points_to_next
                ? `Last refresh ${data.points_to_next.as_of}, from Bilt's Wallet tab.`
                : `No reading yet, so this starts at the full ${RULES.milestonePoints.toLocaleString("en-US")}.`}
            </span>
          </label>
          <RentField rent={data.rent} onSaved={load} />
        </div>

        {plan ? (
          <div className="min-w-0 space-y-6">
            <section className="space-y-3 rounded-2xl border border-neutral-800/70 bg-neutral-900 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <SectionTitle>Bilt Cash on each rent day</SectionTitle>
                <Legend />
              </div>
              <div className="overflow-x-auto">
                <BiltCashChart planned={plan.justEnough.rows} />
              </div>
            </section>
            <MonthTable plan={plan} />
          </div>
        ) : (
          <p className="text-sm text-neutral-400">The plan appears once rent is saved and readable.</p>
        )}
      </div>

      <Rules />
    </section>
  );
}

function Summary({ plan, inputs }: { plan: Plan; inputs: PlanInputs }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {summaryTiles(plan, inputs).map((t) => (
        <div key={t.label} className="space-y-1 rounded-2xl border border-neutral-800/70 bg-neutral-900 p-3">
          <p className="text-xs text-neutral-400">{t.label}</p>
          <p className={`text-2xl font-extrabold tabular-nums [font-stretch:112%] ${TONE[t.tone]}`}>{t.value}</p>
          <p className="text-xs text-neutral-500">{t.hint}</p>
        </div>
      ))}
    </div>
  );
}

function RentField({ rent, onSaved }: { rent: BiltCashDto["rent"]; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [shown, setShown] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (rent.state === "locked") {
    return (
      <div className="rounded-md border border-red-900 bg-red-950/40 p-3 text-xs">
        Rent is saved but can't be read: {rent.message ?? "the encryption key is unavailable"}.
      </div>
    );
  }

  if (rent.state === "unset" || editing) {
    return (
      <form
        className="flex flex-col gap-1 text-xs text-neutral-400"
        onSubmit={(e) => {
          e.preventDefault();
          const amount = parseAmount(text);
          if (amount === null || amount === 0) {
            setError("Enter your monthly rent in dollars.");
            return;
          }
          saveRent(amount)
            .then(() => {
              setEditing(false);
              setText("");
              setError(null);
              onSaved();
            })
            .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
        }}
      >
        <label htmlFor="bilt-rent" className="flex flex-col gap-1">
          {rent.state === "unset" ? "Add your monthly rent" : "Monthly rent"}
          <input
            id="bilt-rent"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className={FIELD}
          />
        </label>
        <span className="text-neutral-500">Stored encrypted. Never shown unless you reveal it.</span>
        <div className="flex gap-2">
          <button type="submit" className="btn btn-primary btn-sm">
            Save rent
          </button>
          {editing && (
            <button type="button" onClick={() => setEditing(false)} className="btn btn-ghost btn-sm">
              Cancel
            </button>
          )}
        </div>
        {error && <span className="text-red-400">{error}</span>}
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-1 text-xs text-neutral-400">
      Monthly rent
      <div className="flex items-center gap-2">
        <span className="mr-auto text-sm text-neutral-100 tabular-nums">
          {shown && rent.amount !== null ? formatExact(rent.amount) : "••••"}
        </span>
        <button type="button" onClick={() => setShown((s) => !s)} className="btn btn-ghost btn-sm">
          {shown ? "Hide" : "Reveal"}
        </button>
        <button type="button" onClick={() => setEditing(true)} className="btn btn-ghost btn-sm">
          Change
        </button>
      </div>
      <span className="text-neutral-500">Stored encrypted.</span>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-neutral-400">
      <span className="flex items-center gap-1.5">
        <i className="inline-block h-0.5 w-3.5 bg-sky-400" />
        At the least spend
      </span>
      <span className="flex items-center gap-1.5">
        <i className="inline-block w-3.5 border-t-2 border-dashed border-amber-400" />
        Rent unlock
      </span>
      <span className="flex items-center gap-1.5">
        <i className="inline-block h-2 w-2 rounded-full bg-amber-400" />
        Rent short
      </span>
    </div>
  );
}

function MonthTable({ plan }: { plan: Plan }) {
  return (
    <section className="space-y-3 rounded-2xl border border-neutral-800/70 bg-neutral-900 p-4">
      <SectionTitle>Month by month</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[38rem] text-right text-sm tabular-nums">
          <thead className="text-xs text-neutral-500">
            <tr>
              <th className="px-2 py-1 text-left font-medium">Month</th>
              <th className="px-2 py-1 font-medium">Rent unlock</th>
              <th className="px-2 py-1 font-medium">Spend before rent</th>
              <th className="px-2 py-1 font-medium">Bilt Cash on rent day</th>
              <th className="px-2 py-1 font-medium">Expired</th>
              <th className="px-2 py-1 font-medium">$50 bonuses</th>
              <th className="px-2 py-1 font-medium">Rent</th>
            </tr>
          </thead>
          <tbody>
            {plan.justEnough.rows.map((r) => (
              <tr key={`${r.year}-${r.month}`} className="border-t border-neutral-800">
                <td className="px-2 py-2 text-left">
                  {monthName(r.month)} {r.year}
                  {r.granted > 0 && <span className="ml-2 text-xs text-neutral-400">+{formatWhole(r.granted)}</span>}
                </td>
                <td className="px-2 py-2">{formatExact(r.unlockCost)}</td>
                <td className="px-2 py-2">
                  {r.spend < EPS ? <span className="text-neutral-500">$0</span> : formatWhole(atLeast(r.spend))}
                </td>
                <td className="px-2 py-2">{formatExact(r.cashOnRentDay)}</td>
                <td className="px-2 py-2">
                  {r.expired > EPS ? (
                    <span className="text-red-400">{formatExact(r.expired)}</span>
                  ) : (
                    <span className="text-neutral-500">—</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  {r.bonuses ? (
                    <span className="text-green-400">+${r.bonuses * RULES.milestoneCash}</span>
                  ) : (
                    <span className="text-neutral-500">—</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  {r.short > EPS ? (
                    <span className="rounded-full bg-amber-950 px-2 py-0.5 text-xs text-amber-400">
                      Short {formatExact(r.short)}
                    </span>
                  ) : (
                    <span className="rounded-full bg-green-950 px-2 py-0.5 text-xs text-green-400">Unlocked</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Rules() {
  const rows: [string, string][] = [
    ["Earn", "4% Bilt Cash on everyday card spend. Housing payments earn none."],
    ["Unlock", "$3 of Bilt Cash unlocks 100 points on rent, up to 1 point per $1, so a full unlock is 3% of rent. Optional, and never a fee."],
    ["Expiry", "Each award expires at the end of the month, 12 months after it is earned, and the oldest is spent first. The plan uses the dates Bilt shows for the balance on hand."],
    ["$50 bonus", "Every 25,000 qualifying points in a calendar year. Rent and everyday points count; promo points don't. Resets Jan 1."],
    ["Points", "2 per $1 of everyday Palladium spend. Bonus categories earn more, so a $50 can land sooner than shown."],
    ["Anniversary", `Palladium adds $${RULES.anniversaryGrant} of Bilt Cash on each card anniversary, kept by opting out of the refreshed benefits.`],
  ];
  return (
    <section className="space-y-2 text-sm">
      <SectionTitle>Rules the plan uses</SectionTitle>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-neutral-400">{k}</dt>
            <dd className="max-w-prose text-neutral-300">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-neutral-500">
        From Bilt's Bilt Cash Terms (updated 2026-10-27), Card 2.0 Program Overview and Rewards Terms, read
        2026-10-27. The anniversary is from Bilt's concierge.
      </p>
    </section>
  );
}
